import React, { useEffect, useMemo, useState, useCallback } from "react";
import { motion, useReducedMotion } from "framer-motion";
import "./Services.css";

import webDevVideo from "../assets/web.mp4";
import digitalmarket from "../assets/Digital.mp4";
import seoVideo from "../assets/seo.mp4";
import smmVideo from "../assets/smm.mp4";
import appdev from "../assets/App.mp4";

const services = [
  { title: "Web Development", video: webDevVideo },
  { title: "Digital Marketing", video: digitalmarket },
  { title: "Search Engine Optimization", video: seoVideo },
  { title: "Social Media Marketing", video: smmVideo },
  { title: "App Development", video: appdev },
];

const wrap = (i, len) => ((i % len) + len) % len;

const relIndex = (i, active, len) => {
  const raw = i - active;
  const alt = raw > 0 ? raw - len : raw + len;
  return Math.abs(alt) < Math.abs(raw) ? alt : raw;
};

const Services = () => {
  const reduceMotion = useReducedMotion();
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);

  const len = services.length;

  const next = useCallback(() => setActive((p) => wrap(p + 1, len)), [len]);
  const prev = useCallback(() => setActive((p) => wrap(p - 1, len)), [len]);

  // autoplay
  useEffect(() => {
    if (paused) return;
    const t = setInterval(next, 3400);
    return () => clearInterval(t);
  }, [paused, next]);

  const transition = useMemo(
    () =>
      reduceMotion
        ? { duration: 0 }
        : { type: "spring", stiffness: 140, damping: 20, mass: 0.9 },
    [reduceMotion]
  );

  const getPose = (r) => {
    if (r === 0) {
      return {
        x: "0%",
        scale: 1,
        opacity: 1,
        rotate: 0,
        filter: "blur(0px)",
        zIndex: 3,
        pointerEvents: "auto",
      };
    }
    if (r === -1) {
      return {
        x: "-46%",
        scale: 0.92,
        opacity: 0.45,
        rotate: -1,
        filter: "blur(1.8px)",
        zIndex: 2,
        pointerEvents: "auto",
      };
    }
    if (r === 1) {
      return {
        x: "46%",
        scale: 0.92,
        opacity: 0.45,
        rotate: 1,
        filter: "blur(1.8px)",
        zIndex: 2,
        pointerEvents: "auto",
      };
    }
    return {
      x: r < 0 ? "-70%" : "70%",
      scale: 0.86,
      opacity: 0,
      rotate: 0,
      filter: "blur(3px)",
      zIndex: 1,
      pointerEvents: "none",
    };
  };

  return (
    <section className="svc-section" id="services">
      <div className="svc-header">
        <h2 className="svc-title">Our Services</h2>
        <div className="svc-title-line" />
      </div>

      <div
        className="svc-carousel"
        onMouseEnter={() => setPaused(true)}
        onMouseLeave={() => setPaused(false)}
        onTouchStart={() => setPaused(true)}
      >
        <div className="svc-stage">
          {services.map((item, i) => {
            const r = relIndex(i, active, len);
            const pose = getPose(r);
            const isCenter = r === 0;

            return (
              <motion.div
                key={i}
                className={`svc-card ${
                  isCenter ? "svc-card--main" : "svc-card--side"
                }`}
                animate={pose}
                transition={transition}
                style={{ zIndex: pose.zIndex, pointerEvents: pose.pointerEvents }}
                onClick={() => {
                  if (r === -1) prev();
                  if (r === 1) next();
                }}
                drag={isCenter ? "x" : false}
                dragConstraints={{ left: 0, right: 0 }}
                dragElastic={0.06}
                onDragEnd={(_, info) => {
                  if (!isCenter) return;
                  if (info.offset.x < -70 || info.velocity.x < -700) next();
                  else if (info.offset.x > 70 || info.velocity.x > 700) prev();
                }}
              >
                <video
                  className="svc-video"
                  src={item.video}
                  autoPlay
                  loop
                  muted
                  playsInline
                  preload="auto"
                />

                {isCenter && <div className="svc-bottom-fade" />}

                {/* Always visible caption */}
                {isCenter && (
                  <div className="svc-caption svc-caption--glass">
                    <h3 className="svc-caption-title" title={item.title}>
                      {item.title}
                    </h3>
                  </div>
                )}
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default Services;
